import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { type FileItem, queryKeys } from "../api/client";
import { uploadFile } from "../api/upload";
import { readStoredToken } from "../auth/token";

export type QueueStatus = "queued" | "uploading" | "done" | "error" | "aborted";

export type QueueItem = {
  id: string;
  file: File;
  status: QueueStatus;
  loaded: number;
  total: number;
  error?: string;
  serverFile?: FileItem;
};

export function useUploadQueue() {
  const qc = useQueryClient();
  const [items, setItems] = useState<QueueItem[]>([]);
  const running = useRef(false);
  const aborters = useRef<Map<string, () => void>>(new Map());

  const update = useCallback((id: string, patch: Partial<QueueItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const drain = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const next = (await new Promise<QueueItem | null>((res) => {
          setItems((prev) => {
            const candidate = prev.find((it) => it.status === "queued");
            res(candidate ?? null);
            return prev;
          });
        })) as QueueItem | null;
        if (!next) break;
        update(next.id, { status: "uploading", loaded: 0, total: next.file.size });
        const token = readStoredToken();
        const handle = uploadFile(next.file, token, (loaded, total) =>
          update(next.id, { loaded, total }),
        );
        aborters.current.set(next.id, handle.abort);
        try {
          const server = await handle.promise;
          update(next.id, { status: "done", serverFile: server, loaded: next.file.size });
          qc.invalidateQueries({ queryKey: ["files"] });
        } catch (e) {
          if ((e as Error).message === "Aborted") {
            update(next.id, { status: "aborted" });
          } else {
            update(next.id, { status: "error", error: (e as Error).message });
          }
        } finally {
          aborters.current.delete(next.id);
        }
      }
    } finally {
      running.current = false;
    }
    qc.invalidateQueries({ queryKey: queryKeys.health });
  }, [qc, update]);

  const enqueue = useCallback(
    (files: File[]) => {
      const additions: QueueItem[] = files.map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        status: "queued",
        loaded: 0,
        total: f.size,
      }));
      setItems((prev) => [...prev, ...additions]);
      setTimeout(() => void drain(), 0);
    },
    [drain],
  );

  const abort = useCallback((id: string) => {
    aborters.current.get(id)?.();
  }, []);

  const clearFinished = useCallback(() => {
    setItems((prev) => prev.filter((it) => it.status === "queued" || it.status === "uploading"));
  }, []);

  return { items, enqueue, abort, clearFinished };
}
